import { FC, useEffect, useMemo } from 'react';
import {
	Box,
	Flex,
	Grid,
	Image,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useBlockchainProvider } from '../../../providers/Blockchain/blockchainProvider';
import { AccountMini } from '../AccountMini/accountMini';
import { privatePages } from '../../App/Wrapper/Wrapper';
import { BrandAssets } from 'src/config';
import { CloseIcon } from '@chakra-ui/icons';
import { LanguageSelect } from '../LanguageSelect/languageSelect';
import { FooterSocials } from '../../Footer/FooterSocials/socials';
import { colors } from 'src/utils/colors';
import { WalletIcon } from 'src/utils/icons/wallet';
import { useMoralis } from 'react-moralis';
import { NetworkSelector } from '../NetworkSelector';

export enum ACCOUNT_TYPES {
	LOGGED,
	NOT_LOGGED,
	LOADING,
}

interface MenuProps {
	onCloseMobileMenu: any;
	isOpenMobileMenu: any;
}

export const Menu: FC<MenuProps> = ({ onCloseMobileMenu, isOpenMobileMenu }) => {
	const { t } = useTranslation();
	const { isLoggedIn, login } = useBlockchainProvider();
	const { isAuthenticated, logout, authenticate } = useMoralis();

	const location = useLocation();
	const { pathname } = location;
	const splitLocation = pathname.split('/');

	// const locationRegex = new RegExp('^(/creator/).*');

	const typeOptions = {
		[ACCOUNT_TYPES.LOGGED]: () => <AccountMini />,
		[ACCOUNT_TYPES.NOT_LOGGED]: () => (
			<Flex
				justifyContent="center"
				alignItems="center"
				gridGap="14px"
				onClick={login}
				cursor="pointer"
			>
				<WalletIcon />
				<Text
					fontSize="16px"
					lineHeight="20px"
					color="white"
					fontWeight="bold"
				>
					{t('menu.connect')}
				</Text>
			</Flex>
		),
	};

	const accountType = useMemo(() => {
		if (!isLoggedIn) {
			return ACCOUNT_TYPES.NOT_LOGGED;
		}
		return ACCOUNT_TYPES.LOGGED;
	}, [isLoggedIn]);

	const menuItems = useMemo(
		() =>
			[
				{
					name: t('menu.add'),
					accent: t('menu.accent'),
					link: '/add',
				},
				{
					name: t('menu.yourGames'),
					link: '/creator/games',
				},
			].filter(item =>
				isAuthenticated && isLoggedIn ? item : privatePages.filter(el => el.path === item.link).length === 0
			),
		[isAuthenticated, t, isLoggedIn]
	);

	return (
		<>
			{/* mobile menu */}
			<Modal
				size="full"
				isOpen={isOpenMobileMenu}
				onClose={onCloseMobileMenu}
			>
				<ModalOverlay />
				<ModalContent
					bg="black"
					p="32px"
					onClick={onCloseMobileMenu}
				>
					<ModalHeader>
						<Flex
							align="center"
							justify="space-between"
						>
							<Link to="/">
								<Image
									maxW="33px"
									maxH="33px"
									src={BrandAssets.hashupLogo}
								/>
							</Link>
							<Flex
								cursor="pointer"
								onClick={onCloseMobileMenu}
								fontWeight="600"
								fontSize="16px"
								gridGap="14px"
								align="center"
							>
								<Text>{t('yourGames.columns.close')}</Text>
								<CloseIcon color={colors.primary} />
							</Flex>
						</Flex>
					</ModalHeader>
					<ModalBody>
						<Flex
							w="100%"
							flexDir="column"
							gridGap="68px"
							align="center"
						>
							<LanguageSelect
								onClickEvent={(event: any) => {
									event.stopPropagation();
								}}
							/>
							<Flex
								flexDir="column"
								justify="center"
								gridGap="41px"
							>
								{menuItems.map(item => (
									<Flex
										key={item.link}
										justify="center"
									>
										<Link to={item.link}>
											<Flex
												color="white"
												fontSize="16px"
												fontWeight={pathname === item.link ? '600' : 'normal'}
												_hover={{ textDecoration: 'none' }}
												position="relative"
											>
												{item.name}
												{item.accent && <Text color={colors.primary}>{item.accent}</Text>}
												{pathname === item.link && (
													<Box
														bgColor={colors.primary}
														h="2px"
														w="30px"
													/>
												)}
											</Flex>
										</Link>
									</Flex>
								))}
							</Flex>
						</Flex>
					</ModalBody>
					<ModalFooter
						flexWrap="wrap"
						gridGap={{ base: '15px', md: '40px' }}
						justifyContent="center"
						display="flex"
						fontSize="12px"
						fontWeight="600"
						onClick={onCloseMobileMenu}
					>
						<Flex
							w="100%"
							justify="center"
						>
							<Image
								src={BrandAssets.hodlLogo}
								alt="hodl"
							/>
						</Flex>
						<Flex gridGap="5px">
							<Flex
								textTransform="uppercase"
								gridGap="4px"
							>
								{t('footer.our')}
								<Text color={colors.primary}> {t('footer.socialMedia')}</Text>
							</Flex>
							<FooterSocials />
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
			{/* desktop menu */}
			<Grid
				display={{ base: 'none', md: 'grid' }}
				templateColumns="202px 1fr auto"
				gridGap="70px"
				m="18px 0px"
				alignItems="center"
			>
				<Box justifySelf="flex-start">
					<Link to="/">
						<Image
							src={BrandAssets.logo}
							alt="Game Contract Logo"
						/>
					</Link>
				</Box>
				<Flex
					gridGap="34px"
					flexWrap="wrap"
					fontSize="14px"
					justifySelf="flex-start"
				>
					{menuItems.map(item => (
						<Link
							key={item.link}
							to={item.link}
						>
							<Flex
								color="white"
								fontSize="16px"
								fontWeight={pathname === item.link ? '600' : 'normal'}
								position="relative"
							>
								{item.name}
								{item.accent && <Text color={colors.primary}>{item.accent}</Text>}
								{pathname === item.link && (
									<Box
										bgColor={colors.primary}
										h="2px"
										w="30px"
										position="absolute"
										left="0"
										bottom="-5px"
									/>
								)}
							</Flex>
						</Link>
					))}
				</Flex>
				<Flex gridGap="20px">
					{accountType != ACCOUNT_TYPES.NOT_LOGGED && <NetworkSelector />}
					{typeOptions[accountType]()}
				</Flex>
			</Grid>
		</>
	);

	//
};
